<script setup lang="ts">
import loudspeaker from '@/assets/img/loudspeaker.png';
import { AppSelectIdVerification } from '@/components/fields';
import { AppButton, AppFormCard, AppFormHeader, AppTypography } from '@/core/components';
import type { SupportedIds } from '@/core/types/SupportedIdVerification';
import { useAuthStore } from '@/modules/auth/store/auth';
import useSnackbarStore from '@/store/snackbar';
import { storeToRefs } from 'pinia';

const props = withDefaults(
  defineProps<{
    title: string;
    isOnboarding: boolean;
  }>(),
  {
    isOnboarding: true,
  }
);

const emits = defineEmits<{
  (e: 'success'): void;
  (e: 'next', next: string): void;
  (e: 'skip', next: string): void;
}>();

const route = useRoute();
const router = useRouter();
const snackbar = useSnackbarStore();
const { user, isClient, isIdDocumentValid } = storeToRefs(useAuthStore());

const {
  fetching,
  frankieOneStatus,
  isVerificationPassed,
  isVerificationFailed,
  isVerificationRefer,
  isVerificationUnchecked,
  isVerificationNone,
  getFrankieOneStatus,
} = useFrankieOne();

const continuePage = ref('');
const connectIdClaims = ref(null);
const { initializeConnectID, loading: loadingConnectID } = useConnectID();

const statusDialog = ref(false);
watch(loadingConnectID, (value: boolean) => {
  statusDialog.value = value;
});

const isRetrievingTokens = ref(false);
watch(isRetrievingTokens, (value: boolean) => {
  statusDialog.value = value;
});

const errorDialog = ref(false);
const errorMessage = ref('Please try again.');

onMounted(async () => {
  // Load ConnectID script dynamically only when this component is mounted
  if (!document.querySelector('script[src*="cid-idp-selector.js"]')) {
    const script = document.createElement('script');
    script.src = 'https://cdn.connectid.com.au/idp-selector/8-latest/cid-idp-selector.js';
    script.defer = true;
    document.head.appendChild(script);
  }

  if (route.query.cb && !route.query?.access_denied) {
    isRetrievingTokens.value = true;
    const response = await fetch('/api/connect-id/retrieve-tokens', {
      method: 'POST',
      body: JSON.stringify({
        code: route.query.code,
        iss: route.query.iss,
        state: route.query.state,
        user_id: user.value?.id,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    isRetrievingTokens.value = false;

    if (!response.ok) {
      const { error } = await response.json();
      errorDialog.value = true;
    } else {
      const { getSession } = useAuth();
      await getSession();

      const { claims, token, nextPage } = await response.json();
      connectIdClaims.value = claims;
      snackbar.showSnackbar({
        text: 'User identify pulled from ConnectID',
        state: true,
      });

      continuePage.value = nextPage;

      emits('success');
    }
  }

  nextTick(async () => {
    if (!isIdDocumentValid.value && !isVerificationPassed.value) {
      let nextPage = isClient.value ? 'profile-profile-information' : 'profile-profile-information-required-documents';
      // check if router has onboarding in path
      if (route.path.includes('onboarding')) {
        nextPage = isClient.value ? 'support-requirements' : 'required-documents';
      }

      await initializeConnectID(nextPage);
    }
  });
});

const statusMessage = computed((): string => {
  if (isRetrievingTokens.value) {
    return 'Retrieving data from ConnectID...';
  } else if (loadingConnectID.value) {
    return 'Redirecting you to ConnectID...';
  } else {
    return 'Redirecting you to FrankieOne...';
  }
});

const submitAttribute = computed(
  (): {
    label: string;
    variant: string;
  } => {
    if (isVerificationPassed.value || isIdDocumentValid.value) {
      return {
        label: 'Your ID verification has been successfully verified.',
        variant: 'text',
      };
    } else if (isVerificationFailed.value || isVerificationRefer.value) {
      return {
        label: 'Verify with FrankieOne',
        variant: 'tonal',
      };
    } else {
      return {
        label: 'Verify with FrankieOne',
        variant: 'tonal',
      };
    }
  }
);

const skipAttribute = computed(
  (): {
    label: string;
    variant: string;
  } => {
    if (isVerificationFailed.value || isVerificationRefer.value) {
      return {
        label: 'Skip',
        variant: 'text',
      };
    } else if (isVerificationPassed.value || isIdDocumentValid.value) {
      return {
        label: 'Continue',
        variant: 'tonal',
      };
    } else {
      return {
        label: 'Complete later',
        variant: 'text',
      };
    }
  }
);

const hasSubmitted = computed((): boolean => {
  return !isVerificationNone.value || !isVerificationUnchecked.value;
});

const pending = ref(false);
async function initialize() {
  pending.value = true;
  if (!isIdDocumentValid.value && frankieOneStatus.value === null) {
    await getFrankieOneStatus();
  }
  pending.value = false;
}

const submit = async () => {
  router.push({
    name: 'frankieone-verification',
    query: {
      from: route.name === 'onboarding' ? 'onboarding' : 'profile',
    },
  });
};

const { VALIDATIONS } = useValidation();
const {
  v$,
  form,
  handleSubmit,
  getErrors: getValidationError,
} = useForm<{ via: SupportedIds }>({
  form: {
    via: null,
  },
  rules: {
    via: {
      required: VALIDATIONS.required('ID'),
    },
  },
});
const { updating, updateSupportedIdVerification } = useSupportedIdVerification();

const errors = computed(() => {
  return getValidationError();
});

const canSubmit = computed((): boolean => {
  return !v$.value.$invalid;
});

const { resetOptions } = useAuthStore();
const save = async (): Promise<void> => {
  await handleSubmit(async function () {
    const response = await updateSupportedIdVerification([form.value]);
    if (response) {
      if (user.value) {
        (user.value as any).fo_verification = response.fo_verification;
      }

      resetOptions();
      if (props.isOnboarding) {
        await navigateTo({ name: 'onboarding', query: { form: 'personal-profile' } });
      } else {
        await reloadNuxtApp();
      }
    }
  });

  if (errors.value.via) {
    snackbar.showSnackbar({
      text: errors.value.via,
      state: 'warning',
    });
  }
};

const initializeForm = (): void => {
  if (user.value && (user.value as any).fo_verification) {
    form.value.via = (user.value as any).fo_verification;
  }
};

const next = async (): Promise<void> => {
  let nextPage = { name: continuePage.value, params: {} } as Record<string, any>;
  if (route.path.includes('onboarding')) {
    nextPage = { name: 'onboarding', query: { form: isClient.value ? 'support-requirements' : 'required-documents' } };
  }

  await navigateTo(nextPage);
};

const connectIdOnValidate = () => {
  const element = document.getElementById('cid-idp-selector-popup');
  if (element) {
    element.style.cssText = 'display: block; z-index: 9999';
  }
};

const showTooltip = ref(false);

// FrankieOne debugging and web worker management
const frankieOneDebugMode = ref(false);
const frankieOneWorkers = ref<Worker[]>([]);

// Handle FrankieOne web workers and debugging
const handleFrankieOneDebugging = () => {
  // Listen for FrankieOne web worker messages
  window.addEventListener('message', (event) => {
    if (event.data && typeof event.data === 'object') {
      // Handle FrankieOne debug messages
      if (event.data.isOpenBeat !== undefined) {
        console.log('FrankieOne Debug:', event.data.isOpenBeat ? 'Debug mode started' : 'Debug mode ended');
        frankieOneDebugMode.value = event.data.isOpenBeat;
      }
      
      // Handle other FrankieOne messages
      if (event.data.type === 'frankieone-worker') {
        console.log('FrankieOne Worker Message:', event.data);
      }
    }
  });

  // Monitor for FrankieOne web workers
  const originalWorker = window.Worker;
  (window as any).Worker = function(this: any, scriptURL: string, options?: WorkerOptions) {
    const worker = new originalWorker(scriptURL, options);
    
    // Check if this is a FrankieOne worker
    if (scriptURL.includes('frankieone') || scriptURL.includes('debugger')) {
      console.log('FrankieOne Worker Created:', scriptURL);
      frankieOneWorkers.value.push(worker);
      
      // Listen for worker messages
      worker.addEventListener('message', (event) => {
        console.log('FrankieOne Worker Message:', event.data);
      });
      
      // Clean up when worker terminates
      worker.addEventListener('error', (error) => {
        console.log('FrankieOne Worker Error:', error);
        const index = frankieOneWorkers.value.indexOf(worker);
        if (index > -1) {
          frankieOneWorkers.value.splice(index, 1);
        }
      });
    }
    
    return worker;
  } as any;
};

// Clean up FrankieOne workers
const cleanupFrankieOneWorkers = () => {
  frankieOneWorkers.value.forEach((worker: Worker) => {
    try {
      worker.terminate();
    } catch (error) {
      console.log('Error terminating FrankieOne worker:', error);
    }
  });
  frankieOneWorkers.value = [];
};

onMounted(() => {
  initializeForm();
  nextTick(() => {
    initialize();
  });
  
  // Initialize FrankieOne debugging handling
  handleFrankieOneDebugging();
});

onUnmounted(() => {
  // Clean up FrankieOne workers when component is destroyed
  cleanupFrankieOneWorkers();
});
</script>

<template>
  <div class="id-selector">
    <AppFormHeader v-if="!isOnboarding" :title="title" :show-button="false" class="mb-5" />

    <!-- <AppFormCard :title="!isOnboarding ? title : ''" class="mx-auto disclaimer-card">
      <template v-if="isOnboarding">
        <AppTypography type="large" element="h1" class="text-center">Before you start...</AppTypography>
        <v-img :src="loudspeaker" class="mx-auto" width="124" height="160" />
      </template>
      <template v-else> </template>
      <AppTypography type="body-2" element="div" class="text-justify">
        Select the ID type you will be using to verify your identity.
      </AppTypography>

      <AppSelectIdVerification v-model="form.via" :error-messages="errors.via" class="pt-6" />

      <AppButton
        v-if="isOnboarding"
        :disabled="!canSubmit || updating"
        :loading="updating"
        block
        color="primary"
        class="mt-4"
        rounded="xl"
        type="button"
        @click="save"
      >
        Continue
      </AppButton>
    </AppFormCard>

    <AppButton
      v-if="!isOnboarding"
      :disabled="!canSubmit || updating"
      :loading="updating"
      block
      color="primary"
      class="mt-4"
      rounded="xl"
      type="button"
      @click="save"
    >
      Continue
    </AppButton> -->

    <div
        v-if="!connectIdClaims"
        class="id-document__actions d-flex flex-column"
        :class="{
          'id-document__actions--submitted': hasSubmitted,
        }"
      >
        <AppButton
          v-if="isOnboarding"
          block
          color="primary"
          class="id-document__actions__skip mt-4"
          type="button"
          :variant="skipAttribute.variant"
          @click="next()"
        >
          {{ skipAttribute.label }}
        </AppButton>
        <AppButton
          v-if="!fetching && !!frankieOneStatus && !isIdDocumentValid"
          block
          color="primary"
          class="id-document__actions__save mt-2"
          type="button"
          :variant="submitAttribute.variant"
          :loading="pending"
          :readonly="isVerificationPassed"
          @click="submit()"
        >
          {{ submitAttribute.label }}
        </AppButton>
      </div>
  </div>

  <v-dialog v-model="statusDialog" width="400px" persistent class="custom-dialog">
    <v-card class="custom-card">
      <template #append>
        <v-btn size="small" icon @click="statusDialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
      <v-card-text class="text-center">
        <div class="d-flex flex-column align-center justify-center">
          <v-progress-circular indeterminate color="primary" size="100" class="mb-4" />
          <span class="ml-2">{{ statusMessage }}</span>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>

  <AppConfirmDialog
    v-model="errorDialog"
    title="Verification Failed"
    :description="errorMessage"
    confirm-only
    confirm-text="Close"
    @confirm="errorDialog = false"
  />
</template>

<style scoped>
.id-selector {
  width: 100%;
  max-width: var(--form-max-width);
  margin: 0 auto;
}

.id-document__actions .v-btn {
  text-transform: none !important;
}

.custom-dialog :deep(.v-card) .v-card-item {
  padding: 16px 8px;
}
</style>
